'use client';

import * as React from 'react';
import { FiX, FiCheck, FiChevronDown } from 'react-icons/fi';

import { Box } from './box';
import { Field } from './form';
import { Badge } from './badge';
import { Stack } from './stack';
import { Text } from './text';
import { Input } from './input';
import { IconButton } from './icon-button';
import { Portal } from './portal';
import { List, ListItem } from './list/list';

const mergeRefs = <T,>(...refs: Array<React.Ref<T> | undefined>) => {
  return (instance: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref && 'current' in ref) {
        (ref as React.MutableRefObject<T | null>).current = instance;
      }
    });
  };
};

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  /**
   * Array of options to display
   */
  options: MultiSelectOption[];

  /**
   * Currently selected values
   */
  value?: string[];

  /**
   * Callback when selection changes
   */
  onChange?: (values: string[]) => void;

  /**
   * Label for the field
   */
  label?: string;

  /**
   * Placeholder text when no items selected
   */
  placeholder?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  hint?: string;

  /**
   * Whether the field is disabled
   */
  disabled?: boolean;

  /**
   * Whether the field is required
   */
  required?: boolean;

  /**
   * Maximum number of items that can be selected
   */
  maxItems?: number;

  /**
   * Whether to show search input
   */
  searchable?: boolean;

  /**
   * Search placeholder
   */
  searchPlaceholder?: string;

  /**
   * No options message
   */
  noOptionsMessage?: string;

  /**
   * Container className
   */
  containerClassName?: string;
}

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      value: rawValue = [],
      onChange,
      label,
      placeholder = 'Select items...',
      error,
      hint,
      disabled,
      required,
      maxItems,
      searchable = true,
      searchPlaceholder = 'Search...',
      noOptionsMessage = 'No options found',
      containerClassName,
    },
    ref
  ) => {
    // Filter out invalid values (undefined, null, empty string)
    const value = React.useMemo(() => {
      return rawValue.filter((v) => v !== undefined && v !== null && v !== '');
    }, [rawValue]);

    const [isOpen, setIsOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Filter options based on search
    const filteredOptions = React.useMemo(() => {
      if (!searchQuery) return options;

      const query = searchQuery.toLowerCase();
      return options.filter((option) => option.label.toLowerCase().includes(query));
    }, [options, searchQuery]);

    // Get selected option labels
    const selectedLabels = React.useMemo(() => {
      return value.map((val) => {
        const option = options.find((opt) => opt.value === val);
        return option ? option.label : val;
      });
    }, [value, options]);

    // Handle option toggle
    const handleToggleOption = (optionValue: string) => {
      if (!onChange) return;

      // Ensure optionValue is valid before checking inclusion
      const isSelected =
        optionValue !== undefined && optionValue !== null && optionValue !== ''
          ? value.includes(optionValue)
          : false;
      let newValue: string[];

      if (isSelected) {
        newValue = value.filter((v) => v !== optionValue);
      } else {
        if (maxItems && value.length >= maxItems) return;
        newValue = [...value, optionValue];
      }

      onChange(newValue);
    };

    // Handle remove tag
    const _handleRemoveTag = (index: number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onChange || disabled) return;

      const newValue = value.filter((_, i) => i !== index);
      onChange(newValue);
    };

    // Handle clear all
    const handleClearAll = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onChange || disabled) return;
      onChange([]);
    };

    // Handle click outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    // Focus search input when opened
    React.useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);

    const invalid = !!error;

    return (
      <Field.Root
        invalid={!!error}
        className={containerClassName}
        disabled={disabled}
        ref={mergeRefs(ref, containerRef)}
      >
        {/* Label */}
        {label && (
          <Box as="label" fontSize="sm" fontWeight="medium" display="block" marginBottom="4px">
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Multi-select trigger */}
        <Box
          position="relative"
          cursor={disabled ? 'not-allowed' : 'pointer'}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          {/* Selected tags or placeholder */}
          <Box
            border="1px solid"
            borderColor={invalid ? '#EF4444' : '#D1D5DB'}
            borderRadius="md"
            padding={2}
            minHeight="40px"
            backgroundColor={disabled ? '#F9FAFB' : '#FFFFFF'}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            _hover={{
              borderColor: disabled ? '#D1D5DB' : '#9CA3AF',
            }}
            _focus={{
              outline: 'none',
              borderColor: '#3B82F6',
              boxShadow: '0 0 0 1px #3B82F6',
            }}
          >
            <Box flex={1}>
              {value.length === 0 ? (
                <Text color="#9CA3AF" fontSize="sm">
                  {placeholder}
                </Text>
              ) : (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {selectedLabels.map((label, index) => (
                    <Badge
                      key={value[index]}
                      size="sm"
                      colorPalette="blue"
                      onRemove={() => {
                        if (!onChange || disabled) return;
                        const newValue = value.filter((_, i) => i !== index);
                        onChange(newValue);
                      }}
                    >
                      {label}
                    </Badge>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Actions */}
            <Stack direction="row" gap={1} alignItems="center">
              {value.length > 0 && (
                <IconButton
                  aria-label="Clear all"
                  size="sm"
                  variant="ghost"
                  onClick={handleClearAll}
                  disabled={disabled}
                  icon={<FiX />}
                />
              )}
              <Box
                color="#9CA3AF"
                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                transition="transform 0.2s"
              >
                <FiChevronDown />
              </Box>
            </Stack>
          </Box>

          {/* Dropdown */}
          {isOpen && (
            <Portal>
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                marginTop={1}
                backgroundColor="#FFFFFF"
                border="1px solid"
                borderColor="#E5E7EB"
                borderRadius="md"
                boxShadow="lg"
                zIndex={1500} // popover level - must be above modal (1400)
                maxHeight="300px"
                overflowY="auto"
                pointerEvents="auto" // Ensure clicks work inside modal dialogs
              >
                {/* Search input */}
                {searchable && (
                  <Box padding={2} borderBottomWidth={1} borderBottomColor="#E5E7EB">
                    <Input
                      ref={searchInputRef}
                      inputSize="sm"
                      placeholder={searchPlaceholder}
                      value={searchQuery}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchQuery(e.target.value)
                      }
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  </Box>
                )}

                {/* Options list */}
                <List styleType="none" spacing={0}>
                  {filteredOptions.length === 0 ? (
                    <Box padding={3} textAlign="center">
                      <Text fontSize="sm" color="#9CA3AF">
                        {noOptionsMessage}
                      </Text>
                    </Box>
                  ) : (
                    filteredOptions.map((option) => {
                      // Ensure option.value is valid before checking inclusion
                      const isSelected =
                        option.value !== undefined && option.value !== null && option.value !== ''
                          ? value.includes(option.value)
                          : false;
                      const disabled =
                        option.disabled ||
                        (maxItems ? value.length >= maxItems && !isSelected : false);

                      return (
                        <ListItem
                          key={option.value}
                          padding={2}
                          cursor={disabled ? 'not-allowed' : 'pointer'}
                          backgroundColor={isSelected ? '#EFF6FF' : 'transparent'}
                          opacity={disabled ? 0.6 : 1}
                          _hover={{
                            backgroundColor: disabled ? 'transparent' : '#F3F4F6',
                          }}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (!disabled) {
                              handleToggleOption(option.value);
                            }
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Text fontSize="sm">{option.label}</Text>
                            {isSelected && (
                              <Box color="#2563EB">
                                <FiCheck />
                              </Box>
                            )}
                          </Stack>
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Box>
            </Portal>
          )}
        </Box>

        {/* Error Message */}
        {error && (
          <Box fontSize="xs" color="#EF4444" marginTop="4px">
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="xs" color="#6B7280" marginTop="4px">
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
